// AI Consent Form Generation Service
import { doc, setDoc, getDoc, updateDoc, collection, serverTimestamp, getDocs, query, where, deleteDoc, deleteField, addDoc, arrayUnion, Timestamp, increment } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions, auth } from '../config/firebase';
import { emailService } from './emailService';
import { authService } from './authService';
import { consentLimitService } from './consentLimitService';
import { consentQaService } from './consentQaService';
import { DENTALLY_INTEGRATION_ENABLED } from '../config/featureFlags';
import { extractPostOpFromSections } from '../utils/postOpSection';
import { generateConsentAccessToken } from '../utils/consentAccessToken';
import { resolveConsentDentistInfo, initialsFromPersonName } from '../utils/consentClinicianDisplay';
import { canPutTotalOnFirstRow } from '../utils/consentCostSection';
import {
  callablePatientGetConsentByToken,
  callablePatientUpdateConsent,
  fetchPatientConsentForSigning,
} from '../utils/patientConsentCallable';

function buildFundingFirestoreFields(funding) {
  if (!funding?.patientFundingType) return {};
  return {
    patientFundingType: funding.patientFundingType,
    nhsBand: funding.nhsBand || null,
    nhsBandCost: funding.nhsBandCost ?? null,
    privateCost: funding.privateCost ?? null,
    isExempt: funding.isExempt === true,
    nhsExempt: funding.nhsExempt === true,
    exemptionReason: funding.exemptionReason || '',
    nhsBandDescription: funding.nhsBandDescription || null,
  };
}

function treatmentPricingTypeFromFunding(patientFundingType) {
  if (patientFundingType === 'private') return 'private';
  if (patientFundingType === 'nhs' || patientFundingType === 'exempt') return 'nhs';
  if (patientFundingType === 'mixed') return 'mixed';
  return 'private';
}

// UUID generation fallback
const generateUUID = () => {
  try {
    const { v4: uuidv4 } = require('uuid');
    return uuidv4();
  } catch (error) {
    // Fallback UUID generation
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
};

export const aiConsentService = {
  // Generate AI consent form based on treatment type
  async generateConsentForm(treatmentData) {
    try {
      const { treatmentType, patientDetails, clinicInfo, additionalNotes, estimatedCost } = treatmentData;
      
      // Check consent limits before creating
      await consentLimitService.enforceConsentLimits(clinicInfo.id);
      
      // Get current user data for dentist tracking
      const userData = await authService.getCurrentUserData();
      
      // Try to get staff information for better name resolution
      let dentistName = 'Unknown';
      let dentistEmail = userData?.email || '';
      let dentistUserId = userData?.uid || '';
      
      if (userData?.uid) {
        try {
          // First try to get from staff collection
          const { collection, query, where, getDocs } = await import('firebase/firestore');
          const { db } = await import('../config/firebase');
          
          // Try multiple queries to find the staff record
          let staffData = null;
          
          // Query 1: By userId
          const staffQueryByUserId = query(collection(db, 'staff'), where('userId', '==', userData.uid));
          const staffSnapshotByUserId = await getDocs(staffQueryByUserId);
          
          if (!staffSnapshotByUserId.empty) {
            staffData = staffSnapshotByUserId.docs[0].data();
          } else {
            // Query 2: By email
            const staffQueryByEmail = query(collection(db, 'staff'), where('email', '==', userData.email));
            const staffSnapshotByEmail = await getDocs(staffQueryByEmail);
            
            if (!staffSnapshotByEmail.empty) {
              staffData = staffSnapshotByEmail.docs[0].data();
            }
          }
          
          if (staffData) {
            // Construct name from staff data
            const firstName = staffData.firstName || '';
            const lastName = staffData.lastName || '';
            const fullName = `${firstName} ${lastName}`.trim();
            
            if (fullName) {
              dentistName = fullName;
              dentistEmail = staffData.email || dentistEmail;
            } else {
              // If no name in staff data, try displayName
              dentistName = staffData.displayName || userData.displayName || 'Unknown';
            }
          } else {
            // No staff record found, use user data
            if (userData?.displayName) {
              dentistName = userData.displayName;
            } else if (userData?.firstName && userData?.lastName) {
              dentistName = `${userData.firstName} ${userData.lastName}`;
            } else if (userData?.firstName) {
              dentistName = userData.firstName;
            } else if (userData?.lastName) {
              dentistName = userData.lastName;
            } else if (userData?.email) {
              // Only use email prefix if we have no other name data
              const emailPrefix = userData.email.split('@')[0];
              // Clean up the email prefix to make it more readable
              dentistName = emailPrefix.charAt(0).toUpperCase() + emailPrefix.slice(1).replace(/[0-9]/g, '');
            }
          }
        } catch (error) {
          console.error('Error getting staff data:', error);
          // Fallback to user data
          if (userData?.displayName) {
            dentistName = userData.displayName;
          } else if (userData?.firstName && userData?.lastName) {
            dentistName = `${userData.firstName} ${userData.lastName}`;
          } else if (userData?.firstName) {
            dentistName = userData.firstName;
          } else if (userData?.lastName) {
            dentistName = userData.lastName;
          } else if (userData?.email) {
            const emailPrefix = userData.email.split('@')[0];
            dentistName = emailPrefix.charAt(0).toUpperCase() + emailPrefix.slice(1).replace(/[0-9]/g, '');
          }
        }
      }
      
      const dentistInfo = {
        name: dentistName,
        initials: this.generateInitials(dentistName),
        email: dentistEmail,
        userId: dentistUserId
      };
      
      // AI-generated consent form content based on treatment type
      const consentContent = await this.getAIGeneratedContent(treatmentType, patientDetails, clinicInfo, additionalNotes);
      
      // Create consent form document
      const consentForm = {
        id: generateUUID(),
        treatmentType,
        patientId: patientDetails.id,
        patientName: patientDetails.name,
        patientEmail: patientDetails.email,
        dateOfBirth: patientDetails.dateOfBirth || null, // Add date of birth for UK consent
        clinicId: clinicInfo.id,
        clinicName: clinicInfo.name,
        content: consentContent,
        status: 'pending',
        estimatedCost: estimatedCost || null,
        treatmentCost: estimatedCost || null, // Also store as treatmentCost for compatibility
        createdAt: serverTimestamp(),
        // expiresAt removed: consent forms do not expire
        accessToken: await this.generateUniqueAccessToken(clinicInfo.id),
        signatures: [],
        dentistInfo: dentistInfo, // Add dentist tracking
        metadata: {
          generateMethod: 'ai',
          treatmentDetails: treatmentData,
          version: '1.0'
        }
      };
      
      // Save to database
      await setDoc(doc(db, 'aiConsents', consentForm.id), consentForm);
      
      // Update clinic's monthly consent usage
      await this.updateClinicConsentUsage(clinicInfo.id);
      
      return consentForm;
    } catch (error) {
      console.error('Error generating AI consent form:', error);
      throw error;
    }
  },

  // Generate initials from full name
  generateInitials(fullName) {
    return initialsFromPersonName(fullName);
  },

  /** Resolve logged-in user as { name, initials, email, userId } (internal / audit). */
  async resolveLoggedInUserInfo() {
    const userData = await authService.getCurrentUserData();
    let name = 'Unknown';
    let email = userData?.email || '';
    const userId = userData?.uid || '';
    if (userData?.uid) {
      try {
        const staffQuery = query(collection(db, 'staff'), where('userId', '==', userData.uid));
        const staffSnap = await getDocs(staffQuery);
        if (!staffSnap.empty) {
          const d = staffSnap.docs[0].data();
          name = [d.firstName, d.lastName].filter(Boolean).join(' ') || d.displayName || name;
          email = d.email || email;
        } else {
          name = userData.displayName || userData.email?.split('@')[0] || name;
        }
      } catch (e) {
        name = userData.displayName || userData.email?.split('@')[0] || name;
      }
    }
    return {
      name,
      initials: this.generateInitials(name),
      email,
      userId,
    };
  },

  /** Who generated the consent — practice name for display, staff id retained for audit. */
  async resolveGeneratedByInfo(clinicInfo) {
    const userInfo = await this.resolveLoggedInUserInfo();
    const practiceName = (clinicInfo?.name || '').trim();
    if (!practiceName) return userInfo;
    return {
      ...userInfo,
      name: practiceName,
      initials: this.generateInitials(practiceName),
    };
  },

  buildTreatingClinicianInfo(name, options = {}) {
    const trimmed = (name || '').trim();
    if (!trimmed) return null;
    return {
      name: trimmed,
      initials: this.generateInitials(trimmed),
      email: options.email || '',
      userId: options.userId || '',
      rosterId: options.rosterId || null,
    };
  },

  // Check if clinic has reached consent limits
  async checkConsentLimits(clinicId) {
    try {
      const { doc, getDoc, collection, query, where, getDocs } = await import('firebase/firestore');
      const { db } = await import('../config/firebase');
      
      // Get clinic subscription info
      const clinicDoc = await getDoc(doc(db, 'clinics', clinicId));
      if (!clinicDoc.exists()) {
        throw new Error('Clinic not found');
      }
      
      const clinicData = clinicDoc.data();
      const subscription = clinicData.subscription || {};
      const { resolveEntitlement } = await import('../utils/grandfathering');
      const entitlement = resolveEntitlement(subscription, new Date(), clinicId);
      const monthlyLimit = entitlement.monthlyConsentLimit;

      if (entitlement.prePlan) {
        throw new Error('No plan selected yet. Please subscribe before generating consents.');
      }
      
      // If unlimited (-1), allow creation
      if (monthlyLimit === -1) {
        return true;
      }
      
      // Calculate current month's usage
      const thisMonth = new Date();
      const startOfMonth = new Date(thisMonth.getFullYear(), thisMonth.getMonth(), 1);
      
      const consentsQuery = query(
        collection(db, 'aiConsents'),
        where('clinicId', '==', clinicId)
      );
      
      const snapshot = await getDocs(consentsQuery);
      const monthlyConsents = [];
      
      snapshot.forEach(doc => {
        const consent = doc.data();
        if (consent.status === 'draft') return;
        const createdDate = consent.createdAt?.toDate?.() || consent.createdAt || new Date(0);
        if (createdDate >= startOfMonth) {
          monthlyConsents.push(consent);
        }
      });
      
      const currentUsage = monthlyConsents.length;
      
      if (currentUsage >= monthlyLimit) {
        throw new Error(`Monthly consent limit reached (${currentUsage}/${monthlyLimit}). Please upgrade your plan or wait until next month.`);
      }
      
      return true;
    } catch (error) {
      console.error('Error checking consent limits:', error);
      throw error;
    }
  },

  // Update clinic's monthly consent usage
  async updateClinicConsentUsage(clinicId) {
    try {
      const { doc, getDoc, updateDoc, serverTimestamp, collection, query, where, getDocs } = await import('firebase/firestore');
      const { db } = await import('../config/firebase');
      
      // Calculate current month's usage
      const thisMonth = new Date();
      const startOfMonth = new Date(thisMonth.getFullYear(), thisMonth.getMonth(), 1);
      
      const consentsQuery = query(
        collection(db, 'aiConsents'),
        where('clinicId', '==', clinicId)
      );
      
      const snapshot = await getDocs(consentsQuery);
      const monthlyConsents = [];
      
      snapshot.forEach(doc => {
        const consent = doc.data();
        if (consent.status === 'draft') return;
        const createdDate = consent.createdAt?.toDate?.() || consent.createdAt || new Date(0);
        if (createdDate >= startOfMonth) {
          monthlyConsents.push(consent);
        }
      });
      
      const currentUsage = monthlyConsents.length;
      
      // Update clinic document
      await updateDoc(doc(db, 'clinics', clinicId), {
        'subscription.consentsUsedThisMonth': currentUsage,
        'subscription.lastUpdated': serverTimestamp()
      });
      
      return currentUsage;
    } catch (error) {
      console.error('Error updating clinic consent usage:', error);
    }
  },

  // Get AI-generated content based on treatment type
  async getAIGeneratedContent(treatmentType, patientDetails, clinicInfo, additionalNotes) {
    // This would integrate with an AI service like OpenAI GPT
    // For now, we'll use template-based generation with dynamic content
    
    const templates = {
      'Root canal therapy': {
        title: 'Endodontic Treatment (Root Canal Therapy) Informed Consent',
        sections: [
          {
            title: 'Treatment Description',
            content: `I understand that root canal therapy involves removing infected or damaged pulp tissue from inside the tooth, cleaning and disinfecting the root canals, and sealing them to prevent further infection.`
          },
          {
            title: 'Benefits of Treatment',
            content: `The benefits include: elimination of pain and infection, preservation of the natural tooth, restoration of normal function, and prevention of further complications.`
          },
          {
            title: 'Risks and Complications',
            content: `Risks may include: post-operative pain, swelling, incomplete healing, need for retreatment, or potential need for extraction if treatment fails.`
          }
        ]
      },
      'Amalgam fillings': {
        title: 'Dental Amalgam Restoration Informed Consent',
        sections: [
          {
            title: 'Treatment Description',
            content: `I understand that amalgam filling treatment involves removing decayed tooth structure and placing a durable metal filling material to restore the tooth's function.`
          },
          {
            title: 'Benefits of Treatment',
            content: `The benefits include: excellent durability, restoration of tooth function, prevention of further decay, and cost-effective treatment.`
          },
          {
            title: 'Risks and Complications',
            content: `Risks may include: temporary sensitivity, potential allergic reaction (rare), or need for replacement over time due to normal wear.`
          }
        ]
      },
      'Composite fillings': {
        title: 'Dental Filling (Composite) Consent Form',
        sections: [
          {
            title: 'Treatment Description',
            content: `I understand that composite filling treatment involves removing decayed tooth structure and placing a tooth-colored filling material to restore the tooth's function and appearance.`
          },
          {
            title: 'Benefits of Treatment',
            content: `The benefits include: natural appearance, restoration of tooth function, prevention of further decay, and preservation of the natural tooth structure.`
          },
          {
            title: 'Risks and Complications',
            content: `Risks may include: temporary sensitivity, potential staining over time, need for replacement sooner than amalgam fillings, or need for additional treatment if decay is extensive.`
          }
        ]
      },
      'Simple extractions': {
        title: 'Tooth Extraction Consent Form',
        sections: [
          {
            title: 'Treatment Description',
            content: `I understand that tooth extraction involves the complete removal of a tooth from its socket in the bone. This may be necessary due to decay, infection, trauma, orthodontic reasons, or other clinical conditions.`
          },
          {
            title: 'Risks and Complications',
            content: `Risks may include: pain, swelling, bleeding, infection, dry socket, numbness, sinus complications (for upper teeth), jaw fracture (rare), or need for additional procedures.`
          },
          {
            title: 'Post-Extraction Care',
            content: `I understand the importance of following post-extraction instructions including: avoiding smoking, using prescribed medications, maintaining oral hygiene, and attending follow-up appointments.`
          }
        ]
      },
      'Crowns': {
        title: 'Dental Crown Treatment Consent Form',
        sections: [
          {
            title: 'Treatment Description',
            content: `I understand that crown treatment involves preparing the tooth, taking impressions, and placing a custom-made restoration that covers the entire visible portion of the tooth to restore function and appearance.`
          },
          {
            title: 'Benefits of Treatment',
            content: `Benefits include: restoration of tooth function, protection of weakened tooth structure, improvement of appearance, and prevention of further damage.`
          },
          {
            title: 'Risks and Complications',
            content: `Risks may include: temporary sensitivity; crown fracture or need for replacement over time. For a vital tooth, preparation and placement may cause loss of nerve vitality and root canal treatment may then be needed as a separate procedure. For a tooth that has already had root canal treatment, the crown protects the treated tooth; re-root canal treatment may rarely be needed if symptoms return — this is not the same risk as a vital tooth having its first crown.`
          }
        ]
      },
      'Braces (metal, ceramic, lingual)': {
        title: 'Orthodontic Treatment Comprehensive Informed Consent',
        sections: [
          {
            title: 'Nature of Orthodontic Treatment',
            content: `I understand that orthodontic treatment involves the use of appliances (braces, aligners, retainers) to move teeth and/or align jaws to improve function, health, and appearance. The treatment involves:\n\n**Types of Appliances:**\n• Fixed braces (metal, ceramic, or lingual)\n• Removable appliances (aligners, retainers, expanders)\n• Auxiliary devices (headgear, elastics, temporary anchorage devices)\n\n**Treatment Process:**\n• Initial placement of orthodontic appliances\n• Regular adjustment appointments every 4-8 weeks\n• Monitoring of progress and treatment modifications\n• Removal of appliances upon completion\n• Retention phase with retainers\n\n**Treatment Goals:**\n• Proper alignment of teeth and jaws\n• Improved function and oral health\n• Enhanced facial aesthetics\n• Stable, long-term results`
          },
          {
            title: 'Treatment Duration and Expectations',
            content: `I understand that orthodontic treatment typically involves:\n\n**Treatment Timeline:**\n• Average treatment time: 18-36 months (varies by complexity)\n• Initial discomfort period: 3-7 days after each adjustment\n• Regular appointments required throughout treatment\n• Retention phase: minimum 2 years, possibly lifelong\n\n**Factors Affecting Treatment Time:**\n• Severity of the orthodontic problem\n• Patient age and growth potential\n• Patient cooperation and compliance\n• Oral hygiene maintenance\n• Attendance at scheduled appointments\n• Biological response to treatment\n\n**Treatment Phases:**\n• Active treatment phase (braces/aligners in place)\n• Retention phase (wearing retainers)\n• Long-term maintenance and monitoring\n\nI understand that treatment time estimates are approximate and may vary based on individual circumstances.`
          },
          {
            title: 'Benefits and Expected Outcomes',
            content: `The anticipated benefits of orthodontic treatment include:\n\n**Functional Benefits:**\n• Improved chewing efficiency and digestion\n• Better speech and pronunciation\n• Reduced risk of dental trauma\n• Easier cleaning and maintenance of teeth\n• Reduced wear on teeth and jaw joints\n• Improved jaw function and comfort\n\n**Health Benefits:**\n• Reduced risk of tooth decay and gum disease\n• Prevention of abnormal tooth wear\n• Decreased risk of jaw joint problems (TMJ)\n• Improved overall oral health\n• Potential reduction in sleep apnea symptoms\n\n**Aesthetic Benefits:**\n• Improved smile appearance\n• Enhanced facial profile and balance\n• Increased self-confidence and self-esteem\n• Better lip support and facial harmony\n\n**Long-term Benefits:**\n• Stable, lasting results with proper retention\n• Improved quality of life\n• Better long-term dental health prognosis\n\nI understand that while orthodontic treatment is generally successful, individual results may vary and no guarantee of specific outcomes can be made.`
          },
          {
            title: 'Risks, Discomfort, and Potential Complications',
            content: `I understand that orthodontic treatment may involve certain risks and discomfort:\n\n**Common Discomfort and Side Effects:**\n• Initial soreness and discomfort after appliance placement and adjustments\n• Difficulty eating certain foods initially\n• Temporary speech changes or lisping\n• Cheek and lip irritation from appliances\n• Increased saliva production initially\n• Temporary loosening of teeth during movement\n\n**Oral Health Risks:**\n• Increased risk of tooth decay and gum disease if oral hygiene is inadequate\n• White spot lesions (permanent staining) around brackets\n• Gum recession or inflammation\n• Bad breath due to food retention around appliances\n\n**Treatment-Related Complications:**\n• Root resorption (shortening of tooth roots)\n• Loss of tooth vitality requiring root canal treatment\n• Allergic reactions to orthodontic materials (rare)\n• Breakage or swallowing of appliance components\n• Injury to lips, cheeks, or tongue from appliances\n\n**Relapse and Stability Issues:**\n• Tendency for teeth to return to original positions\n• Need for lifelong retention to maintain results\n• Changes due to normal growth and aging\n• Wisdom tooth eruption affecting alignment\n\n**Jaw Joint (TMJ) Considerations:**\n• Pre-existing jaw joint problems may be aggravated\n• New jaw joint symptoms may develop (rare)\n• Clicking, popping, or pain in jaw joints\n• Limited mouth opening or jaw locking\n\n**Rare but Serious Complications:**\n• Severe allergic reactions\n• Nerve damage or loss of sensation\n• Ankylosis (fusion of tooth to bone)\n• Need for surgical intervention\n• Incomplete treatment results requiring additional procedures`
          },
          {
            title: 'Patient Responsibilities and Cooperation',
            content: `Successful orthodontic treatment requires my active participation and cooperation:\n\n**Oral Hygiene Requirements:**\n• Brush teeth thoroughly after every meal and snack\n• Use fluoride toothpaste and rinse as recommended\n• Floss daily with special orthodontic floss or tools\n• Use prescribed mouth rinses or supplements\n• Maintain regular dental cleanings every 3-6 months\n\n**Dietary Restrictions:**\n• Avoid hard foods (nuts, ice, hard candy, popcorn)\n• Avoid sticky foods (caramel, gum, taffy)\n• Avoid sugary and acidic foods and drinks\n• Cut food into small pieces rather than biting\n• Avoid habits like nail biting or pen chewing\n\n**Appliance Care:**\n• Wear removable appliances as instructed\n• Keep appliances clean and store properly\n• Report broken or loose appliances immediately\n• Wear elastics (rubber bands) as prescribed\n• Use headgear or other auxiliary appliances as directed\n\n**Appointment Compliance:**\n• Attend all scheduled appointments\n• Arrive on time for appointments\n• Reschedule missed appointments promptly\n• Follow all post-appointment instructions\n• Contact office with questions or concerns\n\n**Activity Modifications:**\n• Wear mouthguards during sports activities\n• Avoid contact sports without proper protection\n• Be cautious with musical instruments\n• Modify habits that could damage appliances`
          },
          {
            title: 'Alternative Treatment Options',
            content: `Alternative approaches to orthodontic treatment include:\n\n**1. No Treatment:**\n• Acceptance of current tooth and jaw alignment\n• Potential for worsening of problems over time\n• Continued difficulty with function or aesthetics\n• Possible increased risk of dental problems\n\n**2. Limited Treatment:**\n• Addressing only specific problem areas\n• Shorter treatment time and lower cost\n• May not achieve optimal results\n• Possible need for future comprehensive treatment\n\n**3. Surgical Options:**\n• Orthognathic surgery for severe jaw discrepancies\n• Faster correction of major skeletal problems\n• Higher cost and surgical risks\n• Combined orthodontic and surgical approach\n\n**4. Cosmetic Alternatives:**\n• Veneers or crowns for appearance improvement\n• Does not address underlying bite problems\n• May require tooth reduction\n• Shorter lifespan than orthodontic correction\n\n**5. Different Appliance Types:**\n• Clear aligners (Invisalign) vs. traditional braces\n• Lingual braces (behind teeth) vs. conventional braces\n• Self-ligating vs. traditional bracket systems\n• Each option has specific advantages and limitations\n\nI have been informed of these alternatives and understand their respective benefits and limitations.`
          },
          {
            title: 'Retention and Long-Term Stability',
            content: `Following active orthodontic treatment, I understand that:\n\n**Retention Phase Requirements:**\n• Retainers must be worn as prescribed (typically full-time initially)\n• Gradual reduction to nighttime wear only\n• Possible lifelong nighttime retention recommended\n• Regular retainer check appointments\n• Replacement of retainers as needed\n\n**Types of Retainers:**\n• Removable retainers (Hawley, clear plastic)\n• Fixed retainers (bonded wire behind teeth)\n• Combination of removable and fixed retainers\n• Each type has specific care requirements\n\n**Factors Affecting Stability:**\n• Individual biological tendency for relapse\n• Growth and aging changes\n• Wisdom tooth eruption\n• Oral habits (tongue thrusting, grinding)\n• Periodontal (gum) disease\n• General health conditions\n\n**Long-Term Expectations:**\n• Some minor changes in tooth position are normal over time\n• Perfect alignment may not be maintained indefinitely\n• Periodic orthodontic evaluation may be recommended\n• Additional treatment may be needed in the future\n• Commitment to retention is essential for maintaining results\n\n**Consequences of Poor Retention Compliance:**\n• Rapid return of teeth toward original positions\n• Loss of treatment benefits\n• Possible need for retreatment\n• Additional cost and time investment\n• Potential for more complex problems to develop`
          },
          {
            title: 'Financial Considerations and Treatment Policies',
            content: `I understand the financial aspects of orthodontic treatment:\n\n**Treatment Fees:**\n• Comprehensive treatment fee covers active treatment phase\n• Separate fees for retention appliances and monitoring\n• Additional charges for broken appliances or extra appointments\n• Fees for extended treatment beyond estimated time\n• Costs for retreatment if retention is not followed\n\n**Payment Options:**\n• Payment plans and financing options available\n• Insurance benefits and limitations explained\n• My responsibility for any unpaid balances\n• Consequences of missed payments\n\n**Treatment Policies:**\n• Missed appointment fees may apply\n• Broken appliance replacement costs\n• Treatment may be discontinued for non-compliance\n• Records and appliances may be withheld for unpaid balances\n\n**Insurance Considerations:**\n• Orthodontic benefits typically have lifetime maximums\n• Coverage varies by insurance plan\n• Pre-authorization may be required\n• I am responsible for understanding my benefits\n• Claims processing and reimbursement procedures\n\n**Value of Treatment:**\n• Investment in long-term oral health and function\n• Improved quality of life and self-confidence\n• Prevention of future dental problems\n• Lasting benefits with proper retention and care`
          }
        ]
      },
      'Clear aligners (Invisalign, etc.)': {
        title: 'Clear Aligner Treatment Consent Form',
        sections: [
          {
            title: 'Treatment Description',
            content: `I understand that clear aligner treatment involves wearing a series of custom-made, removable aligners that gradually move teeth into proper alignment.`
          },
          {
            title: 'Treatment Requirements',
            content: `I understand that aligners must be worn 20-22 hours per day, changed as prescribed, and proper oral hygiene must be maintained throughout treatment.`
          },
          {
            title: 'Risks and Complications',
            content: `Risks may include: temporary discomfort, speech changes, need for additional aligners, or potential need for refinements.`
          }
        ]
      },
      'Dental implant placement': {
        title: 'Dental Implant Treatment Comprehensive Informed Consent',
        sections: [
          {
            title: 'Nature of Dental Implant Treatment',
            content: `I understand that dental implant treatment is a multi-phase procedure that involves:\n\n**Phase 1 - Implant Placement:**\n• Surgical placement of a titanium implant into the jawbone\n• Use of local anesthesia and/or sedation\n• Possible bone grafting if insufficient bone is present\n• Healing period of 3-6 months for osseointegration\n\n**Phase 2 - Abutment Placement:**\n• Minor surgical procedure to expose the implant\n• Placement of healing abutment or impression coping\n• Gum tissue healing period of 2-6 weeks\n\n**Phase 3 - Restoration:**\n• Impressions for custom crown, bridge, or denture\n• Fabrication and placement of final restoration\n• Adjustments and final fitting\n\nThe entire process typically takes 4-9 months depending on individual healing and complexity.`
          },
          {
            title: 'Pre-Treatment Requirements and Evaluation',
            content: `I understand that successful implant treatment requires:\n\n**Medical Evaluation:**\n• Complete medical and dental history review\n• Assessment of medications that may affect healing\n• Evaluation of systemic conditions (diabetes, osteoporosis, etc.)\n• Discussion of smoking cessation if applicable\n\n**Diagnostic Requirements:**\n• Comprehensive clinical examination\n• Radiographic evaluation (X-rays, possibly CT scan)\n• Assessment of bone quality and quantity\n• Evaluation of gum tissue health\n• Bite analysis and space assessment\n\n**Prerequisites for Treatment:**\n• Adequate bone volume and density\n• Healthy gum tissues\n• Good oral hygiene\n• Realistic expectations\n• Commitment to maintenance care`
          },
          {
            title: 'Benefits and Expected Outcomes',
            content: `The anticipated benefits of dental implant treatment include:\n\n**Functional Benefits:**\n• Restoration of normal chewing ability\n• Improved speech and pronunciation\n• Prevention of bone loss in the jaw\n• Preservation of adjacent teeth (no need to prepare neighboring teeth)\n• Stable, secure restoration that doesn't slip or move\n\n**Aesthetic Benefits:**\n• Natural appearance and feel\n• Improved facial support and profile\n• Enhanced smile and confidence\n• No visible clasps or metal components\n\n**Long-term Benefits:**\n• Potential to last a lifetime with proper care\n• Maintenance of jawbone structure\n• No dietary restrictions\n• Easy maintenance with regular oral hygiene\n\nI understand that while dental implants have a high success rate (90-95%), no guarantee of success can be made.`
          },
          {
            title: 'Risks, Complications, and Potential Problems',
            content: `I understand that dental implant treatment carries risks and potential complications:\n\n**Surgical Risks:**\n• Infection at the implant site\n• Injury or damage to surrounding structures (teeth, blood vessels, nerves)\n• Sinus problems (for upper jaw implants)\n• Bleeding and swelling\n• Pain and discomfort\n• Allergic reaction to medications or materials\n\n**Implant-Related Complications:**\n• Implant failure or rejection (5-10% of cases)\n• Failure of osseointegration (implant doesn't fuse with bone)\n• Implant mobility or loosening\n• Fracture of the implant or restoration\n• Peri-implantitis (infection around the implant)\n• Need for additional procedures or implant removal\n\n**Anatomical Considerations:**\n• Nerve damage resulting in numbness or tingling\n• Sinus perforation or complications\n• Damage to adjacent teeth or existing restorations\n• Inadequate bone healing\n• Need for bone grafting procedures\n\n**Long-term Considerations:**\n• Need for maintenance and possible replacement of restoration\n• Potential for mechanical complications\n• Changes in gum tissue over time\n• Possible need for additional procedures`
          },
          {
            title: 'Alternative Treatment Options',
            content: `Alternative treatments to dental implants include:\n\n**1. Removable Partial or Complete Dentures:**\n• Less expensive initial cost\n• Non-surgical option\n• May affect speech and eating\n• Requires adhesives and frequent adjustments\n• Continued bone loss over time\n• Less stable and secure\n\n**2. Fixed Bridge:**\n• Requires preparation (grinding) of adjacent healthy teeth\n• Good function and aesthetics\n• Shorter lifespan than implants\n• Difficult to clean underneath\n• Risk to supporting teeth\n\n**3. No Treatment:**\n• Continued bone loss and facial changes\n• Difficulty eating certain foods\n• Possible shifting of remaining teeth\n• Potential impact on speech\n• Aesthetic concerns\n\nI have been informed of these alternatives and understand their respective advantages and disadvantages.`
          },
          {
            title: 'Post-Surgical Care and Healing',
            content: `Following implant surgery, I understand that:\n\n**Immediate Post-Operative Care (First 48-72 hours):**\n• Some bleeding, swelling, and discomfort is normal\n• Ice packs should be applied as directed\n• Prescribed medications must be taken as instructed\n• Soft diet and careful oral hygiene are essential\n• No smoking or alcohol consumption\n• Avoid strenuous activity\n\n**Healing Period (First 2 weeks):**\n• Sutures may need to be removed\n• Gradual return to normal diet as tolerated\n• Gentle oral hygiene around surgical site\n• Follow-up appointments are crucial\n• Report any unusual symptoms immediately\n\n**Osseointegration Period (3-6 months):**\n• Implant must remain undisturbed to allow bone integration\n• Temporary restoration may be provided\n• Regular monitoring appointments\n• Continued excellent oral hygiene\n• Avoid excessive force on the implant area\n\n**Factors Affecting Healing:**\n• Smoking significantly increases risk of failure\n• Diabetes and other medical conditions may affect healing\n• Medications may impact bone formation\n• Poor oral hygiene can lead to infection\n• Excessive force or trauma can cause failure`
          },
          {
            title: 'Long-Term Maintenance and Care',
            content: `For long-term success of dental implants, I understand that:\n\n**Daily Home Care:**\n• Meticulous oral hygiene with specialized tools\n• Regular brushing and flossing around implants\n• Use of antimicrobial rinses as recommended\n• Avoidance of tobacco products\n\n**Professional Maintenance:**\n• Regular dental checkups every 3-6 months\n• Professional cleanings with implant-specific instruments\n• Periodic X-rays to monitor bone levels\n• Immediate attention to any problems or concerns\n\n**Lifestyle Considerations:**\n• Avoid using teeth as tools\n• Wear night guard if grinding or clenching\n• Maintain overall health and manage systemic conditions\n• Report any changes in medical status or medications\n\n**Warning Signs to Report:**\n• Persistent pain or discomfort\n• Swelling or bleeding around implant\n• Looseness or mobility of restoration\n• Changes in bite or fit\n• Any unusual symptoms or concerns`
          }
        ]
      }
    };

    // Try to find an exact match first, then look for partial matches
    let template = templates[treatmentType];
    
    if (!template) {
      // Look for partial matches (case-insensitive)
      const treatmentTypeLower = treatmentType.toLowerCase();
      for (const [key, value] of Object.entries(templates)) {
        if (key.toLowerCase().includes(treatmentTypeLower) || treatmentTypeLower.includes(key.toLowerCase())) {
          template = value;
          break;
        }
      }
    }
    
    // If still no match, create a generic template based on the treatment type
    if (!template) {
      template = {
        title: `${treatmentType} Consent Form`,
        sections: [
          {
            title: 'Treatment Description',
            content: `I understand that ${treatmentType} is a dental procedure that has been recommended for my oral health. I have been informed about the nature of this treatment and what it involves.`
          },
          {
            title: 'Benefits of Treatment',
            content: `The benefits of this treatment include: improvement of oral health, restoration of function, relief from symptoms, and prevention of further dental problems.`
          },
          {
            title: 'Risks and Complications',
            content: `I understand that no medical or dental procedure is 100% successful. Risks may include but are not limited to: pain, swelling, bleeding, infection, need for additional treatment, or other complications.`
          },
          {
            title: 'Alternative Treatments',
            content: `I have been informed about alternative treatment options and understand the risks and benefits of these alternatives.`
          }
        ]
      };
    }
    
    // Personalize the content
    let personalizedContent = {
      ...template,
      patientName: patientDetails.name,
      clinicName: clinicInfo.name,
      treatmentDate: new Date().toLocaleDateString(),
      additionalNotes: additionalNotes || '',
      generatedAt: new Date().toISOString()
    };

    // Add patient-specific information
    personalizedContent.sections.unshift({
      title: 'Patient Information',
      content: `Patient Name: ${patientDetails.name}\nDate of Birth: ${patientDetails.dateOfBirth || 'Not provided'}\nTreatment Date: ${new Date().toLocaleDateString()}\nTreating Clinic: ${clinicInfo.name}`
    });

    if (additionalNotes) {
      personalizedContent.sections.push({
        title: 'Additional Notes',
        content: additionalNotes
      });
    }

    // Add consent and signature section
    personalizedContent.sections.push({
      title: 'Patient Consent',
      content: `I acknowledge that I have read and understand this consent form. I have had the opportunity to ask questions, and all my questions have been answered to my satisfaction. I understand the risks, benefits, and alternatives to the proposed treatment. I give my informed consent for the treatment described above.`
    });

    return personalizedContent;
  },

  // Generate secure access token (short, URL-friendly — used in patient emails)
  generateSecureToken() {
    return generateConsentAccessToken(12);
  },

  /** Ensure token is unique before save. Scoped to clinicId so security rules allow the query. */
  async generateUniqueAccessToken(clinicId = null) {
    const cid = clinicId && String(clinicId).trim() ? String(clinicId).trim() : null;
    for (let attempt = 0; attempt < 8; attempt++) {
      const token = generateConsentAccessToken(attempt > 3 ? 14 : 12);
      try {
        const q = cid
          ? query(
              collection(db, 'aiConsents'),
              where('clinicId', '==', cid),
              where('accessToken', '==', token)
            )
          : query(collection(db, 'aiConsents'), where('accessToken', '==', token));
        const snap = await getDocs(q);
        if (snap.empty) return token;
      } catch (err) {
        const denied =
          err?.code === 'permission-denied'
          || String(err?.message || '').toLowerCase().includes('permission');
        if (denied) {
          return generateConsentAccessToken(16);
        }
        throw err;
      }
    }
    return generateConsentAccessToken(16);
  },

  // Get clinic's consent forms
  async getClinicConsents(clinicId, filters = {}) {
    try {
      console.log('Loading AI consents for clinic:', clinicId);
      
      // Query AI consents for this clinic
      const q = query(
        collection(db, 'aiConsents'),
        where('clinicId', '==', clinicId)
      );
      
      const snapshot = await getDocs(q);
      let consents = [];
      
      snapshot.forEach(doc => {
        const consent = { id: doc.id, ...doc.data() };
        consents.push(consent);
      });
      
      console.log('Found AI consents:', consents.length);
      
      // Filter out deleted consents
      consents = consents.filter(c => !c.isDeleted);
      
      console.log('AI consents after filtering deleted:', consents.length);
      
      // Apply filters client-side
      if (filters.status) {
        consents = consents.filter(c => c.status === filters.status);
      }
      
      // Sort by creation date
      consents.sort((a, b) => {
        const dateA = a.createdAt?.toDate?.() || a.createdAt || new Date(0);
        const dateB = b.createdAt?.toDate?.() || b.createdAt || new Date(0);
        return dateB - dateA;
      });
      
      return consents;
    } catch (error) {
      console.error('Error getting clinic consents:', error);
      throw error;
    }
  },

  // Generate consent form from template
  async generateConsentFromTemplate(templateData) {
    try {
      const { 
        templateId, 
        patientDetails, 
        clinicInfo, 
        additionalNotes, 
        estimatedCost, 
        tooth, 
        diagnosis, 
        clinicianConfirmation,
        dentalDiagnosisData,
        selectedTemplates = [],
        templates: inputTemplates = [],
        // NHS Treatment Information
        treatmentType,
        nhsBand,
        nhsBandDescription,
        privateTreatmentDetails,
        mixedTreatmentDetails
      } = templateData;
      
      console.log('🔍 aiConsentService - NHS Treatment Data Received:', {
        treatmentType,
        nhsBand,
        nhsBandDescription,
        privateTreatmentDetails,
        mixedTreatmentDetails
      });
      
      // Check consent limits before creating
      await this.checkConsentLimits(clinicInfo.id);
      
      // Get current user data for dentist tracking
      const userData = await authService.getCurrentUserData();
      
      // Try to get staff information for better name resolution
      let dentistName = 'Unknown';
      let dentistEmail = userData?.email || '';
      let dentistUserId = userData?.uid || '';
      
      if (userData?.uid) {
        try {
          // First try to get from staff collection
          const { collection, query, where, getDocs } = await import('firebase/firestore');
          const { db } = await import('../config/firebase');
          
          // Try multiple queries to find the staff record
          let staffData = null;
          
          // Query 1: By userId
          const staffQueryByUserId = query(collection(db, 'staff'), where('userId', '==', userData.uid));
          const staffSnapshotByUserId = await getDocs(staffQueryByUserId);
          
          if (!staffSnapshotByUserId.empty) {
            staffData = staffSnapshotByUserId.docs[0].data();
          } else {
            // Query 2: By email
            const staffQueryByEmail = query(collection(db, 'staff'), where('email', '==', userData.email));
            const staffSnapshotByEmail = await getDocs(staffQueryByEmail);
            
            if (!staffSnapshotByEmail.empty) {
              staffData = staffSnapshotByEmail.docs[0].data();
            }
          }
          
          if (staffData) {
            // Construct name from staff data
            const firstName = staffData.firstName || '';
            const lastName = staffData.lastName || '';
            const fullName = `${firstName} ${lastName}`.trim();
            
            if (fullName) {
              dentistName = fullName;
              dentistEmail = staffData.email || dentistEmail;
            } else {
              // If no name in staff data, try displayName
              dentistName = staffData.displayName || userData.displayName || 'Unknown';
            }
          } else {
            // No staff record found, use user data
            if (userData?.displayName) {
              dentistName = userData.displayName;
            } else if (userData?.firstName && userData?.lastName) {
              dentistName = `${userData.firstName} ${userData.lastName}`;
            } else if (userData?.firstName) {
              dentistName = userData.firstName;
            } else if (userData?.lastName) {
              dentistName = userData.lastName;
            } else if (userData?.email) {
              // Only use email prefix if we have no other name data
              const emailPrefix = userData.email.split('@')[0];
              // Clean up the email prefix to make it more readable
              dentistName = emailPrefix.charAt(0).toUpperCase() + emailPrefix.slice(1).replace(/[0-9]/g, '');
            }
          }
        } catch (error) {
          console.error('Error getting staff data:', error);
          // Fallback to user data
          if (userData?.displayName) {
            dentistName = userData.displayName;
          } else if (userData?.firstName && userData?.lastName) {
            dentistName = `${userData.firstName} ${userData.lastName}`;
          } else if (userData?.firstName) {
            dentistName = userData.firstName;
          } else if (userData?.lastName) {
            dentistName = userData.lastName;
          } else if (userData?.email) {
            const emailPrefix = userData.email.split('@')[0];
            dentistName = emailPrefix.charAt(0).toUpperCase() + emailPrefix.slice(1).replace(/[0-9]/g, '');
          }
        }
      }
      
      const dentistInfo = {
        name: dentistName,
        initials: this.generateInitials(dentistName),
        email: dentistEmail,
        userId: dentistUserId
      };
      
      // Get template(s)
      let template = null;
      let templates = null;
      
      if (Array.isArray(templateId)) {
        // Multiple templates
        const templatePromises = templateId.map(id => getDoc(doc(db, 'consentTemplates', id)));
        const templateDocs = await Promise.all(templatePromises);
        templates = templateDocs.map(doc => ({ id: doc.id, ...doc.data() })).filter(t => t.id);
        
        if (templates.length === 0) {
          throw new Error('No valid templates found');
        }
      } else {
        // Single template
        const templateDoc = await getDoc(doc(db, 'consentTemplates', templateId));
        if (!templateDoc.exists()) {
          throw new Error('Template not found');
        }
        template = { id: templateDoc.id, ...templateDoc.data() };
      }
      
      // Personalize content
      let personalizedContent;
      let processedTemplates = [];
      
      console.log('🔍 Template processing - inputTemplates:', inputTemplates);
      console.log('🔍 Template processing - inputTemplates length:', inputTemplates?.length);
      
      if (inputTemplates && inputTemplates.length > 0) {
        // Multiple templates: keep them separate
        processedTemplates = inputTemplates.map(t => ({
          ...t,
          sections: this.personalizeTemplateContent(t, patientDetails, clinicInfo, additionalNotes, tooth, diagnosis).sections
        }));
        
        console.log('🔍 Template processing - processedTemplates:', processedTemplates);
        console.log('🔍 Template processing - processedTemplates length:', processedTemplates.length);
        
        personalizedContent = {
          title: 'Multiple Treatment Consent',
          sections: [] // Keep main content minimal, templates will be displayed separately
        };
      } else {
        // Single template: original behavior
        personalizedContent = this.personalizeTemplateContent(template, patientDetails, clinicInfo, additionalNotes, tooth, diagnosis, treatmentType, nhsBand);
      }

      // Create consent form document
      const consentForm = {
        id: generateUUID(),
        templateId: templateId,
        treatmentType: treatmentType || template?.treatmentType || (templates && templates.length === 1 ? templates[0].treatmentType : 'Multiple'),
        treatmentFor: template?.name || (templates && templates.length === 1 ? templates[0].name : 'Multiple Treatments'), // Add treatmentFor for display
        patientId: patientDetails.id,
        patientName: patientDetails.name,
        patientEmail: patientDetails.email,
        dateOfBirth: patientDetails.dateOfBirth || null, // Add date of birth for UK consent
        clinicId: clinicInfo.id,
        clinicName: clinicInfo.name,
        content: personalizedContent,
        status: 'pending',
        estimatedCost: estimatedCost || null,
        treatmentCost: estimatedCost || null, // Also store as treatmentCost for compatibility
        tooth: tooth,
        diagnosis: diagnosis,
        clinicianConfirmation: clinicianConfirmation || null, // Add clinician confirmation
        dentalDiagnosisData: dentalDiagnosisData || null, // Add dental diagnosis data
        // NHS Treatment Information
        nhsBand: nhsBand || null,
        nhsBandDescription: nhsBandDescription || null,
        privateTreatmentDetails: privateTreatmentDetails || null,
        mixedTreatmentDetails: mixedTreatmentDetails || null,
        createdAt: serverTimestamp(),
        // expiresAt removed: consent forms do not expire
        accessToken: await this.generateUniqueAccessToken(clinicInfo.id),
        signatures: [],
        dentistInfo: dentistInfo, // Add dentist tracking
        templates: processedTemplates.length > 0 ? processedTemplates : null, // Add processed templates
        selectedTemplates: selectedTemplates || [], // Add selected template IDs
        // Guardian information for under-16 patients
        guardianDetails: templateData.guardianDetails || null,
        patientAge: templateData.patientAge || null,
        metadata: {
          generateMethod: 'template',
          templateId: templateId,
          templateName: template?.name,
          patientDetails: patientDetails,
          clinicInfo: clinicInfo,
          version: '1.0'
        }
      };

      // Debug logging
      console.log('🔍 Consent creation - processedTemplates length:', processedTemplates.length);
      console.log('🔍 Consent creation - templates field:', processedTemplates.length > 0 ? processedTemplates : null);
      console.log('🔍 Consent creation - selectedTemplates:', selectedTemplates);
      console.log('🔍 Consent creation - NHS fields in consent object:', {
        treatmentType: consentForm.treatmentType,
        nhsBand: consentForm.nhsBand,
        nhsBandDescription: consentForm.nhsBandDescription,
        privateTreatmentDetails: consentForm.privateTreatmentDetails,
        mixedTreatmentDetails: consentForm.mixedTreatmentDetails
      });
      console.log('🔍 Consent creation - consentForm object:', consentForm);

      // Save to database
      await setDoc(doc(db, 'aiConsents', consentForm.id), consentForm);
      // Update clinic's monthly consent usage
      await this.updateClinicConsentUsage(clinicInfo.id);
      // Fetch the saved consent from Firestore to ensure all fields (including accessToken) are present
      const savedDoc = await getDoc(doc(db, 'aiConsents', consentForm.id));
      const savedConsent = { id: savedDoc.id, ...savedDoc.data() };
      
      return savedConsent;
    } catch (error) {
      console.error('Error generating consent from template:', error);
      throw error;
    }
  },

  // Personalize template content with patient and clinic information
  personalizeTemplateContent(template, patientDetails, clinicInfo, additionalNotes, tooth, diagnosis, treatmentType, nhsBand) {
    // Start with the template content, but filter out sections that will be added separately
    let filteredSections = (template.sections || []).filter(section => 
      section.title !== 'Patient Information' && 
      section.title !== 'Patient Consent' && 
      section.title !== 'Additional Notes'
    );
    
    let personalizedContent = {
      title: template.name,
      sections: [...filteredSections]
    };

    // Build patient information content
    let patientInfoContent = `Patient Name: ${patientDetails.name}
Date of Birth: ${patientDetails.dateOfBirth || 'Not provided'}
Treatment Date: ${new Date().toLocaleDateString()}
Treating Clinic: ${clinicInfo.name}
Tooth/Area: ${tooth}
Diagnosis/Reason: ${diagnosis}`;

    // Add treatment type and NHS band if available
    if (treatmentType) {
      patientInfoContent += `\nTreatment Type: ${treatmentType.charAt(0).toUpperCase() + treatmentType.slice(1)}`;
    }
    if (nhsBand) {
      patientInfoContent += `\nNHS Band: Band ${nhsBand}`;
    }

    // Add patient-specific information section at the beginning
    const patientInfoSection = {
      title: 'Patient Information',
      content: patientInfoContent
    };
    personalizedContent.sections.unshift(patientInfoSection);

    // Add additional notes if provided
    if (additionalNotes) {
      personalizedContent.sections.push({
        title: 'Additional Notes',
        content: additionalNotes
      });
    }

    // Add consent and signature section
    personalizedContent.sections.push({
      title: 'Patient Consent',
      content: `I acknowledge that I have read and understand this consent form. I have had the opportunity to ask questions, and all my questions have been answered to my satisfaction. I understand the risks, benefits, and alternatives to the proposed treatment. I give my informed consent for the treatment described above.`
    });

    return personalizedContent;
  },

  /** Load raw aiConsents document by access token (direct id or accessToken field). */
  async _fetchAiConsentDocByToken(accessToken) {
    const directDoc = await getDoc(doc(db, 'aiConsents', accessToken));
    if (directDoc.exists()) {
      return { id: directDoc.id, ...directDoc.data() };
    }

    const consentsRef = collection(db, 'aiConsents');
    const q = query(consentsRef, where('accessToken', '==', accessToken));
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      throw new Error('Consent form not found or expired');
    }
    const consentDoc = snapshot.docs[0];
    return { id: consentDoc.id, ...consentDoc.data() };
  },

  /** Record patient opened the consent link (not email inbox). Skipped for clinic preview. */
  async recordPatientOpened(consentId, consent, options = {}) {
    if (options.trackOpened === false || !consentId) return null;

    const status = String(consent?.status || '').toLowerCase();
    const shouldPromoteStatus = status === 'sent' || status === 'pending';

    let ipAddress = 'unknown';
    try {
      ipAddress = await authService.getClientIP();
    } catch (_) {
      /* optional */
    }

    const payload = {
      accessLog: arrayUnion({
        action: 'opened',
        timestamp: new Date(),
        ipAddress,
      }),
      lastUpdated: serverTimestamp(),
    };

    if (shouldPromoteStatus) {
      payload.status = 'opened';
    }
    if (!consent.openedAt && (shouldPromoteStatus || status === 'opened')) {
      payload.openedAt = serverTimestamp();
    }

    try {
      await updateDoc(doc(db, 'aiConsents', consentId), payload);
    } catch (err) {
      console.warn('recordPatientOpened: failed to update consent', err);
      return null;
    }

    return {
      status: shouldPromoteStatus ? 'opened' : consent.status,
      openedAt: consent.openedAt || new Date(),
    };
  },

  async _enrichAiConsentWithClinic(consent) {
    let clinicData = null;
    if (consent.clinicId) {
      try {
        const clinicDoc = await getDoc(doc(db, 'clinics', consent.clinicId));
        if (clinicDoc.exists()) {
          clinicData = clinicDoc.data();
        }
      } catch (error) {
        console.warn('Could not load clinic data:', error);
      }
    }

    const logoUrl = clinicData?.branding?.logo || clinicData?.logoUrl || null;
    return {
      ...consent,
      clinicLogo: logoUrl,
      clinicAddress: clinicData?.address || null,
      clinicPhone: clinicData?.phone || null,
      clinicEmail: clinicData?.email || null,
    };
  },

  // Get consent form by access token (always server-side — token is patient auth)
  async getConsentByToken(accessToken, options = {}) {
    console.log('aiConsentService: getConsentByToken called with token:', accessToken);
    try {
      return await fetchPatientConsentForSigning(accessToken, options);
    } catch (error) {
      console.error('aiConsentService: Error getting consent by token:', error);
      throw error;
    }
  },

  // Submit signed consent form
  async submitSignedConsent(accessToken, signatureData) {
    try {
      const bundle = await callablePatientGetConsentByToken(accessToken, { trackOpened: false });
      const consent = bundle.consent;

      const signature = {
        id: generateUUID(),
        patientName: signatureData.patientName,
        signatureImage: signatureData.signatureImage,
        ipAddress: signatureData.ipAddress,
        userAgent: signatureData.userAgent,
        timestamp: new Date(),
        consentVersion: consent?.metadata?.version,
        ...(signatureData.guardianRelationship && {
          guardianRelationship: signatureData.guardianRelationship,
        }),
      };

      const ukComplianceData = {};
      if (signatureData.patientAge !== undefined) ukComplianceData.patientAge = signatureData.patientAge;
      if (signatureData.guardianAvailable !== undefined) ukComplianceData.guardianAvailable = signatureData.guardianAvailable;
      if (signatureData.gillickAssessment) ukComplianceData.gillickAssessment = signatureData.gillickAssessment;
      if (signatureData.parentalConsent) ukComplianceData.parentalConsent = signatureData.parentalConsent;
      if (signatureData.childConsent) ukComplianceData.childConsent = signatureData.childConsent;
      if (signatureData.ukCompliance) ukComplianceData.ukCompliance = signatureData.ukCompliance;

      return callablePatientUpdateConsent(accessToken, 'signed', {
        status: 'signed',
        signatures: [...(consent?.signatures || []), signature],
        signedAt: new Date(),
        ...ukComplianceData,
      });
    } catch (error) {
      console.error('Error submitting signed consent:', error);
      throw error;
    }
  },

  // Handle patient questions
  async submitPatientQuestions(accessToken, questions) {
    try {
      await callablePatientUpdateConsent(accessToken, 'questions', {
        status: 'has_questions',
        patientQuestions: questions,
        questionsSubmittedAt: new Date(),
      });
      try {
        const consent = (await callablePatientGetConsentByToken(accessToken, { trackOpened: false })).consent;
        const thread = await consentQaService.ensureThread({
          consentCollection: 'aiConsents',
          consentDocId: consent.id,
          consentId: consent.id,
          clinicId: consent.clinicId,
          clinicName: consent.clinicName || '',
          accessToken: consent.accessToken || accessToken,
          patientName: consent.patientName || 'Patient',
          patientEmail: consent.patientEmail || '',
          treatmentLabel: consent.treatmentType || consent.treatmentFor || '',
        });
        await consentQaService.sendPatientMessage({
          thread: { ...thread, accessToken: thread.accessToken || accessToken },
          text: questions,
          meta: { source: 'ai_consent_signing' },
          accessToken,
        });
      } catch (threadErr) {
        console.warn('[aiConsentService] Q&A thread update failed:', threadErr?.message || threadErr);
      }
      return { success: true };
    } catch (error) {
      console.error('Error submitting patient questions:', error);
      throw error;
    }
  },

  // Handle consent decline
  async declineConsent(accessToken, reason) {
    try {
      await callablePatientUpdateConsent(accessToken, 'declined', {
        status: 'declined',
        declineReason: reason,
        declinedAt: new Date(),
      });
      return { success: true };
    } catch (error) {
      console.error('Error declining consent:', error);
      throw error;
    }
  },

  // Log consent activity
  async logConsentActivity(consentId, action, details) {
    try {
      let ipAddress = 'unknown';
      try {
        ipAddress = await authService.getClientIP();
      } catch (ipErr) {
        console.error('Failed to fetch IP address:', ipErr);
      }

      await addDoc(collection(db, 'consentActivities'), {
        consentId,
        action,
        details,
        timestamp: serverTimestamp(),
        ipAddress: ipAddress,
        userAgent: navigator.userAgent,
        createdAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error logging consent activity:', error);
    }
  },

  // Notify clinic staff
  async notifyClinicStaff(clinicId, type, data) {
    try {
      await addDoc(collection(db, 'notifications'), {
        clinicId,
        type,
        data,
        status: 'pending',
        createdAt: serverTimestamp()
      });
    } catch (error) {
      console.error('Error notifying clinic staff:', error);
    }
  },

  async getConsentById(consentId) {
    const docRef = doc(db, 'aiConsents', consentId);
    const docSnap = await getDoc(docRef);
    if (!docSnap.exists()) throw new Error('Consent not found');
    return { id: docSnap.id, ...docSnap.data() };
  },

  /** Linked patient profile email first (matches resend); consent snapshot if no patient id or no email on file. */
  async recordReminderSent(consentId, options = {}) {
    const {
      to = '',
      source = 'manual',
      sentByName = null,
      sentByUserId = null,
      emailSource = null,
    } = options;
    const logEntry = {
      action: source === 'automated' ? 'reminder_automated' : 'reminder_manual',
      to: (to || '').trim(),
      at: Timestamp.now(),
      ...(sentByName ? { sentBy: sentByName } : {}),
      ...(sentByUserId ? { sentByUserId } : {}),
      ...(emailSource ? { emailSource } : {}),
    };
    await updateDoc(doc(db, 'aiConsents', consentId), {
      lastReminderAt: serverTimestamp(),
      remindersSent: increment(1),
      emailDeliveryLog: arrayUnion(logEntry),
    });
  },

  async resolveDeliveryEmailForConsent(consent) {
    if (consent?.patientId) {
      try {
        const patientDoc = await getDoc(doc(db, 'patients', consent.patientId));
        if (patientDoc.exists()) {
          const live = (patientDoc.data().email || '').trim();
          if (live) return live;
        }
      } catch (e) {
        console.warn('[aiConsentService] resolveDeliveryEmailForConsent:', e?.message || e);
      }
    }
    return (consent?.patientEmail || '').trim() || null;
  },

  async deleteConsent(consentId, isDelete = true) {
    try {
      const { doc, updateDoc } = await import('firebase/firestore');
      const { db } = await import('../config/firebase');
      await updateDoc(doc(db, 'aiConsents', consentId), { isDeleted: isDelete });
      return true;
    } catch (error) {
      console.error('Error updating consent deleted status:', error);
      throw error;
    }
  },

  /** Appointment day (YYYY-MM-DD). The consent stays on Today until it is signed. */
  async setNeededBy(consentId, neededBy) {
    const consentRef = doc(db, 'aiConsents', consentId);
    if (!neededBy) {
      await updateDoc(consentRef, {
        neededBy: deleteField(),
        neededByUpdatedAt: serverTimestamp(),
      });
      return;
    }
    if (!/^\d{4}-\d{2}-\d{2}$/.test(String(neededBy))) {
      throw new Error('Choose a valid appointment day.');
    }
    await updateDoc(consentRef, {
      neededBy: String(neededBy),
      neededByUpdatedAt: serverTimestamp(),
    });
  },

  // Update consent status
  async updateConsentStatus(consentId, status, options = {}) {
    try {
      console.log('Updating consent status:', { consentId, status });
      const consentRef = doc(db, 'aiConsents', consentId);
      const payload = {
        status: status,
        lastUpdated: serverTimestamp()
      };
      const initialTo = options.initialEmailTo?.trim?.();
      if (status === 'sent') {
        payload.sentAt = serverTimestamp();
        if (initialTo) {
          payload.patientEmail = initialTo;
          payload.emailDeliveryLog = arrayUnion({
            action: 'initial_send',
            to: initialTo,
            at: Timestamp.now()
          });
        }
      }
      await updateDoc(consentRef, payload);
      console.log('Consent status updated successfully');
    } catch (error) {
      console.error('Error updating consent status:', error);
      throw error;
    }
  },

  // --- New AI-generated consent flow (OpenAI via Cloud Function) ---

  /** Call Firebase Function to generate consent content from OpenAI. Returns { sections }. */
  async generateAIConsentContent(payload) {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('You must be logged in to generate a consent form.');
    }
    // Force a fresh ID token so the callable receives valid auth (avoids 401 on production)
    await user.getIdToken(true);
    const generateAIConsent = httpsCallable(functions, 'generateAIConsent');
    const res = await generateAIConsent({
      patientName: payload.patientName,
      dateOfBirth: payload.dateOfBirth || null,
      diagnosis: payload.diagnosis,
      treatments: payload.treatments,
      tooth: payload.tooth || null,
      totalCost: payload.totalCost != null && payload.totalCost !== '' ? Number(payload.totalCost) : null,
      dentistName: payload.dentistName || null,
      clinicName: payload.clinicName || null,
    });
    const data = res.data;
    if (!data || !data.sections || !Array.isArray(data.sections)) {
      throw new Error(data?.message || 'Consent generation failed. Please try again.');
    }
    return { sections: data.sections };
  },

  /** Save AI-generated consent to Firestore (same shape as template consents for signing/email). */
  async saveAIGeneratedConsent({
    patientDetails,
    clinicInfo,
    treatments,
    totalCost,
    sections,
    dentistInfo,
    dentallyPatientId,
    dentallyPatientName,
    funding,
    videos,
    videosConfirmedBy,
  }, options = {}) {
    const asDraft = options.asDraft === true;
    if (!asDraft) {
      await this.checkConsentLimits(clinicInfo.id);
    }
    const generatedByInfo = await this.resolveGeneratedByInfo(clinicInfo);
    const dentist = resolveConsentDentistInfo(dentistInfo, generatedByInfo, clinicInfo.name);
    const treatmentFor = Array.isArray(treatments) && treatments.length > 0
      ? (treatments.length === 1 ? (treatments[0].treatment || treatments[0].diagnosis) : 'Multiple treatments')
      : 'Dental treatment';
    const firstDiagnosis = Array.isArray(treatments) && treatments.length > 0 ? (treatments[0].diagnosis || '') : '';
    const firstTooth = (Array.isArray(treatments) && treatments[0]?.tooth) ? treatments[0].tooth : null;
    // Build dentalDiagnosisData so PDF and views show all treatments (not just first)
    const totalCostNum = totalCost != null && totalCost !== '' ? Number(totalCost) : null;
    const treatmentCosts = Array.isArray(treatments) ? treatments.map(t => (typeof t.cost === 'number' && !isNaN(t.cost) ? t.cost : null)) : [];
    const sumOfTreatmentCosts = treatmentCosts.reduce((s, c) => s + (c ?? 0), 0);
    const effectiveTotal = totalCostNum != null ? totalCostNum : (sumOfTreatmentCosts > 0 ? sumOfTreatmentCosts : null);
    const pricingType = treatmentPricingTypeFromFunding(funding?.patientFundingType);
    const dentalDiagnosisData = Array.isArray(treatments) && treatments.length > 0
      ? {
          diagnosis: treatments.map(t => t.diagnosis || '').filter(Boolean).join('; ') || firstDiagnosis,
          involvedTeeth: treatments.map(t => t.tooth || '').filter(Boolean).join(', ') || firstTooth || '',
          treatments: treatments.map((t, i) => {
            const rowCost = typeof t.cost === 'number' && !isNaN(t.cost) ? t.cost : null;
            const useTotalOnFirst = effectiveTotal != null && rowCost == null && i === 0
              && canPutTotalOnFirstRow(funding?.patientFundingType, sumOfTreatmentCosts);
            return {
              toothNumber: t.tooth || '',
              tooth: t.tooth || '',
              diagnosis: t.diagnosis || '',
              treatmentName: t.treatment || '',
              treatment: t.treatment || '',
              pulpStatus: t.pulpStatus || '',
              extractionType: t.extractionType || '',
              extractionRctOption: t.extractionRctOption || '',
              sedation: t.sedation || '',
              implantStaging: t.implantStaging || '',
              implantAugmentation: t.implantAugmentation || '',
              rctVisitPlan: t.rctVisitPlan || '',
              rctKind: t.rctKind || '',
              dentureType: t.dentureType || '',
              dentureConstruction: t.dentureConstruction || '',
              dentureMaterial: t.dentureMaterial || '',
              dentureArch: t.dentureArch || '',
              perioApproach: t.perioApproach || '',
              orthoAppliance: t.orthoAppliance || '',
              orthoRetention: t.orthoRetention || '',
              softTissueHistology: t.softTissueHistology || '',
              softTissueClosure: t.softTissueClosure || '',
              crownMaterial: t.crownMaterial || '',
              crownType: t.crownType || '',
              crownCore: t.crownCore || '',
              crownSituation: t.crownSituation || '',
              bridgeType: t.bridgeType || '',
              bridgeMaterial: t.bridgeMaterial || '',
              veneerMaterial: t.veneerMaterial || '',
              veneerPrep: t.veneerPrep || '',
              fillingMaterial: t.fillingMaterial || '',
              fillingDepth: t.fillingDepth || '',
              whiteningMode: t.whiteningMode || '',
              pmcTechnique: t.pmcTechnique || '',
              pmcSeparators: t.pmcSeparators || '',
              pmcPulpTherapy: t.pmcPulpTherapy || '',
              implantSoftTissueGraft: t.implantSoftTissueGraft || '',
              localAnaesthetic: t.localAnaesthetic || '',
              facialToxinAreas: t.facialToxinAreas || '',
              facialFillerSites: t.facialFillerSites || '',
              facialVisit: t.facialVisit || '',
              facialNumbing: t.facialNumbing || '',
              pricingType,
              estimatedCost: rowCost ?? (useTotalOnFirst ? effectiveTotal : null),
              customPrice: rowCost ?? (useTotalOnFirst ? effectiveTotal : null),
            };
          }),
        }
      : null;
    const consentId = generateUUID();
    const accessToken = await this.generateUniqueAccessToken(clinicInfo.id);
    const videoPayload = Array.isArray(videos) ? videos : [];
    const templates = [{
      id: 'ai-generated',
      name: 'Treatment Consent',
      title: 'Treatment Consent',
      sections: sections.map(s => ({ title: s.title || 'Section', content: s.content || '' })),
    }];
    const content = { title: treatmentFor, sections };
    const { postOp: extractedPostOp } = extractPostOpFromSections(sections);
    const consentDoc = {
      id: consentId,
      patientId: patientDetails.id,
      patientName: patientDetails.name,
      patientEmail: patientDetails.email,
      dateOfBirth: patientDetails.dateOfBirth || null,
      clinicId: clinicInfo.id,
      clinicName: clinicInfo.name,
      content,
      templates,
      status: asDraft ? 'draft' : 'pending',
      estimatedCost: effectiveTotal ?? null,
      treatmentCost: effectiveTotal ?? null,
      treatmentFor,
      diagnosis: firstDiagnosis,
      tooth: firstTooth,
      dentalDiagnosisData: dentalDiagnosisData || null,
      ...buildFundingFirestoreFields(funding),
      createdAt: serverTimestamp(),
      accessToken,
      signatures: [],
      dentistInfo: dentist,
      generatedByInfo,
      metadata: { generateMethod: 'ai', version: '1.0' },
      videos: videoPayload,
      ...(videosConfirmedBy ? { videosConfirmedBy, videosConfirmedAt: serverTimestamp() } : {}),
      ...(extractedPostOp?.instructions?.length
        ? {
            postOp: {
              title: extractedPostOp.title || 'Post-operative instructions',
              intro: extractedPostOp.intro || '',
              instructions: extractedPostOp.instructions,
              redFlags: extractedPostOp.redFlags || [],
            },
          }
        : {}),
      ...(DENTALLY_INTEGRATION_ENABLED && dentallyPatientId ? { dentallyPatientId, dentallyPatientName: dentallyPatientName || null } : {}),
    };
    await setDoc(doc(db, 'aiConsents', consentId), consentDoc);
    if (!asDraft) {
      await this.updateClinicConsentUsage(clinicInfo.id);
    }
    const saved = await getDoc(doc(db, 'aiConsents', consentId));
    return { id: saved.id, ...saved.data() };
  },

  /** Update an existing AI consent document (e.g. draft) with newly generated or edited sections. */
  async updateAIGeneratedConsentContent(consentId, {
    patientDetails,
    clinicInfo,
    treatments,
    totalCost,
    sections,
    dentistInfo,
    dentallyPatientId,
    dentallyPatientName,
    funding,
    videos,
    videosConfirmedBy,
  }) {
    const generatedByInfo = await this.resolveGeneratedByInfo(clinicInfo);
    const dentist = resolveConsentDentistInfo(dentistInfo, generatedByInfo, clinicInfo.name);
    const treatmentFor = Array.isArray(treatments) && treatments.length > 0
      ? (treatments.length === 1 ? (treatments[0].treatment || treatments[0].diagnosis) : 'Multiple treatments')
      : 'Dental treatment';
    const firstDiagnosis = Array.isArray(treatments) && treatments.length > 0 ? (treatments[0].diagnosis || '') : '';
    const firstTooth = (Array.isArray(treatments) && treatments[0]?.tooth) ? treatments[0].tooth : null;
    const totalCostNum = totalCost != null && totalCost !== '' ? Number(totalCost) : null;
    const treatmentCosts = Array.isArray(treatments) ? treatments.map(t => (typeof t.cost === 'number' && !isNaN(t.cost) ? t.cost : null)) : [];
    const sumOfTreatmentCosts = treatmentCosts.reduce((s, c) => s + (c ?? 0), 0);
    const effectiveTotal = totalCostNum != null ? totalCostNum : (sumOfTreatmentCosts > 0 ? sumOfTreatmentCosts : null);
    const pricingType = treatmentPricingTypeFromFunding(funding?.patientFundingType);
    const dentalDiagnosisData = Array.isArray(treatments) && treatments.length > 0
      ? {
          diagnosis: treatments.map(t => t.diagnosis || '').filter(Boolean).join('; ') || firstDiagnosis,
          involvedTeeth: treatments.map(t => t.tooth || '').filter(Boolean).join(', ') || firstTooth || '',
          treatments: treatments.map((t, i) => {
            const rowCost = typeof t.cost === 'number' && !isNaN(t.cost) ? t.cost : null;
            const useTotalOnFirst = effectiveTotal != null && rowCost == null && i === 0
              && canPutTotalOnFirstRow(funding?.patientFundingType, sumOfTreatmentCosts);
            return {
              toothNumber: t.tooth || '',
              tooth: t.tooth || '',
              diagnosis: t.diagnosis || '',
              treatmentName: t.treatment || '',
              treatment: t.treatment || '',
              pulpStatus: t.pulpStatus || '',
              extractionType: t.extractionType || '',
              extractionRctOption: t.extractionRctOption || '',
              sedation: t.sedation || '',
              implantStaging: t.implantStaging || '',
              implantAugmentation: t.implantAugmentation || '',
              rctVisitPlan: t.rctVisitPlan || '',
              rctKind: t.rctKind || '',
              dentureType: t.dentureType || '',
              dentureConstruction: t.dentureConstruction || '',
              dentureMaterial: t.dentureMaterial || '',
              dentureArch: t.dentureArch || '',
              perioApproach: t.perioApproach || '',
              orthoAppliance: t.orthoAppliance || '',
              orthoRetention: t.orthoRetention || '',
              softTissueHistology: t.softTissueHistology || '',
              softTissueClosure: t.softTissueClosure || '',
              crownMaterial: t.crownMaterial || '',
              crownType: t.crownType || '',
              crownCore: t.crownCore || '',
              crownSituation: t.crownSituation || '',
              bridgeType: t.bridgeType || '',
              bridgeMaterial: t.bridgeMaterial || '',
              veneerMaterial: t.veneerMaterial || '',
              veneerPrep: t.veneerPrep || '',
              fillingMaterial: t.fillingMaterial || '',
              fillingDepth: t.fillingDepth || '',
              whiteningMode: t.whiteningMode || '',
              pmcTechnique: t.pmcTechnique || '',
              pmcSeparators: t.pmcSeparators || '',
              pmcPulpTherapy: t.pmcPulpTherapy || '',
              implantSoftTissueGraft: t.implantSoftTissueGraft || '',
              localAnaesthetic: t.localAnaesthetic || '',
              facialToxinAreas: t.facialToxinAreas || '',
              facialFillerSites: t.facialFillerSites || '',
              facialVisit: t.facialVisit || '',
              facialNumbing: t.facialNumbing || '',
              pricingType,
              estimatedCost: rowCost ?? (useTotalOnFirst ? effectiveTotal : null),
              customPrice: rowCost ?? (useTotalOnFirst ? effectiveTotal : null),
            };
          }),
        }
      : null;
    const videoPayload = Array.isArray(videos) ? videos : [];
    const templates = [{
      id: 'ai-generated',
      name: 'Treatment Consent',
      title: 'Treatment Consent',
      sections: sections.map(s => ({ title: s.title || 'Section', content: s.content || '' })),
    }];
    const content = { title: treatmentFor, sections };
    const { postOp: extractedPostOp } = extractPostOpFromSections(sections);
    await updateDoc(doc(db, 'aiConsents', consentId), {
      patientId: patientDetails.id,
      patientName: patientDetails.name,
      patientEmail: patientDetails.email,
      dateOfBirth: patientDetails.dateOfBirth || null,
      clinicId: clinicInfo.id,
      clinicName: clinicInfo.name,
      content,
      templates,
      estimatedCost: effectiveTotal ?? null,
      treatmentCost: effectiveTotal ?? null,
      treatmentFor,
      diagnosis: firstDiagnosis,
      tooth: firstTooth,
      dentalDiagnosisData: dentalDiagnosisData || null,
      ...buildFundingFirestoreFields(funding),
      dentistInfo: dentist,
      generatedByInfo,
      updatedAt: serverTimestamp(),
      videos: videoPayload,
      ...(videosConfirmedBy ? { videosConfirmedBy, videosConfirmedAt: serverTimestamp() } : {}),
      ...(extractedPostOp?.instructions?.length
        ? {
            postOp: {
              title: extractedPostOp.title || 'Post-operative instructions',
              intro: extractedPostOp.intro || '',
              instructions: extractedPostOp.instructions,
              redFlags: extractedPostOp.redFlags || [],
            },
          }
        : {}),
      ...(DENTALLY_INTEGRATION_ENABLED && dentallyPatientId ? { dentallyPatientId, dentallyPatientName: dentallyPatientName || null } : {}),
    });
  },
};
